import {APP_INITIALIZER, ApplicationConfig, isDevMode, provideZoneChangeDetection,} from '@angular/core';
import {provideRouter} from '@angular/router';
import {provideStore} from '@ngrx/store';
import {provideEffects} from '@ngrx/effects';
import {provideStoreDevtools} from '@ngrx/store-devtools';
import {provideHttpClient} from '@angular/common/http';
import {provideAnimations} from '@angular/platform-browser/animations';
import {registerLocaleData} from '@angular/common';
import en from '@angular/common/locales/en';
import {NZ_ICONS} from 'ng-zorro-antd/icon';
import * as AllIcons from '@ant-design/icons-angular/icons';
import {IconDefinition} from '@ant-design/icons-angular';
import {en_US, NZ_I18N} from 'ng-zorro-antd/i18n';

import {routes} from './app.routes';
import {AppInitService} from './core/services/app-init.service';
import {LocationsEffects} from './store/locations/locations.effects';
import {metaReducers, reducers} from './store';
import {AuthEffects} from './store/auth/auth.effects';
import {TranslocoHttpLoader} from './transloco-loader';
import {provideTransloco} from '@jsverse/transloco';
import {provideTranslocoPersistLang} from '@jsverse/transloco-persist-lang';

// Register locale data
registerLocaleData(en);

// Register icons
const antDesignIcons = AllIcons as Record<string, IconDefinition>;
const icons: IconDefinition[] = Object.keys(antDesignIcons).map(
  (key) => antDesignIcons[key]
);

// Factory function for APP_INITIALIZER
export function initializeApp(appInitService: AppInitService) {
  return () => appInitService.initializeApp();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(),
    provideStore(reducers, { metaReducers }),
    provideEffects([AuthEffects, LocationsEffects]),
    provideAnimations(),
    { provide: NZ_ICONS, useValue: icons },
    { provide: NZ_I18N, useValue: en_US },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppInitService],
      multi: true,
    },
    provideStoreDevtools({
      maxAge: 25,
      logOnly: false,
      autoPause: false,
      trace: false,
      traceLimit: 75,
      connectInZone: true,
      serialize: true,
      features: {
        persist: true,
        lock: false,
        export: true,
        import: 'custom',
        jump: true,
        skip: false,
        reorder: true,
        dispatch: true,
        test: false,
      },
    }),
    provideHttpClient(),
    provideTransloco({
      config: {
        availableLangs: ['en', 'fr', 'rw'],
        defaultLang: 'en',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoPersistLang({
      storage: {
        useValue: localStorage,
      },
    }),
  ],
};
